import React, { useState, useContext, useMemo } from 'react';
import styled from 'styled-components';

import { Modal } from '@/ui/Modal';
import { useTeamsWithUsersQueryRemote, useAddTeamsToLearningJourneyCohortMutationRemote, RTeamUser } from '@/store/v2';
import { ManagementSearchPhrase } from '@/features/management/manager/atoms/ManagementSearchPhrase';
import { JourneyAddTeamsModalTeamItem } from '@/features/journey/admin/atoms/JourneyAddTeamsModalTeamItem';
import { Button } from '@/ui/Button';
import { getErrorResponseMessage } from '@/utils/gqlErrors';
import { SharedSnackbarContext, SnackbarType } from '@/component/SharedSnackbar/SharedSnackbar';
import { PageLoader } from '@/components/Pageloader/Pageloader';
import { JourneyAddTeamsModalBanner } from '@/features/journey/admin/atoms/JourneyAddTeamsModalBanner';

const SearchPhraseContainerStyled = styled.div`
  width: 296px;
`;

const AddedTeamsCountStyled = styled.div`
  display: flex;
  justify-content: end;
  ${(p) => p.theme.typography.body3};
  color: ${(p) => p.theme.colors.neutral.onBackground};
`;

const TeamListStyled = styled.div<{ $isShortened: boolean }>`
  height: calc(100vh - 360px);
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  margin: 24px 0;

  ${({ $isShortened }) => {
    return $isShortened
      ? 'height: calc(100vh - 512px);'
      : `
      height: calc(100vh - 360px);
    `;
  }}
`;

const TeamContainerStyled = styled.div`
  margin: 16px 0;
`;

type JourneyAddTeamsModalProps = {
  open: boolean;
  onClose: () => void;
  journeyId: number;
};

export const JourneyAddTeamsModal = ({ open, onClose, journeyId }: JourneyAddTeamsModalProps): React.ReactElement => {
  const [searchPhrase, setSearchPhrase] = useState('');
  const { openSnackbar } = useContext(SharedSnackbarContext);
  const [newlyAddedTeams, setNewlyAddedTeams] = useState<number[]>([]);
  const [addTeamsToLearningJourney, { loading: mutationLoading }] = useAddTeamsToLearningJourneyCohortMutationRemote();

  const { data, loading } = useTeamsWithUsersQueryRemote({
    variables: {
      filter: {
        searchPhrase,
      },
    },
  });

  const handleSave = async () => {
    const { data, errors } = await addTeamsToLearningJourney({
      variables: {
        input: {
          teamIds: newlyAddedTeams,
          journeyId,
        },
      },
      update(cache, { data }) {
        if (data?.addTeamsToLearningJourneyCohort?.success) {
          cache.evict({ fieldName: 'reportingMyLearningJourneysLearners' });
          cache.evict({ fieldName: 'reportingMyLearningJourneysLearnersTotalCount' });
          cache.evict({ fieldName: 'learningJourneySession' });
        }
      },
    });

    if (errors) {
      const message = getErrorResponseMessage(errors);

      return openSnackbar({
        message: `There were errors when trying to add teams: ${message}`,
        type: SnackbarType.DANGER,
      });
    } else if (data?.addTeamsToLearningJourneyCohort?.success) {
      onClose();

      return openSnackbar({
        message: `${data?.addTeamsToLearningJourneyCohort?.learnersAdded?.length || 0} learners added`,
        type: SnackbarType.SUCCESS,
      });
    }
  };

  const handleClose = () => {
    setNewlyAddedTeams([]);
    onClose();
  };

  const handleAddTeam = (teamId: number, isAdded: boolean) => {
    if (isAdded) {
      setNewlyAddedTeams(newlyAddedTeams?.filter((itemTeamId) => itemTeamId !== teamId));
      return;
    }

    setNewlyAddedTeams([...newlyAddedTeams, teamId]);
  };

  const allUsersFromNewTeams = useMemo(() => {
    const allUsersList: RTeamUser[] = [];

    data?.teams?.data?.forEach((team) => {
      if (!newlyAddedTeams?.includes(team?.id)) return;

      allUsersList.push(...(team?.teamUsers?.total ? (team?.teamUsers?.data as RTeamUser[]) : []));
    });

    return allUsersList;
  }, [data, newlyAddedTeams]);

  const isBannerVisible = useMemo(() => {
    return !!allUsersFromNewTeams?.length;
  }, [allUsersFromNewTeams]);

  return (
    <Modal open={open} onClose={handleClose} size="mobileLandscape">
      <Modal.Title>Add teams</Modal.Title>
      <Modal.Contents>
        <SearchPhraseContainerStyled>
          <ManagementSearchPhrase
            onChange={(newPhrase) => setSearchPhrase(newPhrase)}
            value={searchPhrase}
            placeholder="Search team by name"
            fullWidth
          />
        </SearchPhraseContainerStyled>
        {newlyAddedTeams?.length > 0 && !loading && (
          <AddedTeamsCountStyled>
            {`${newlyAddedTeams?.length} team${newlyAddedTeams?.length > 1 ? 's' : ''} added`}
          </AddedTeamsCountStyled>
        )}
        <TeamListStyled $isShortened={isBannerVisible}>
          {loading ? (
            <PageLoader />
          ) : (
            <>
              {data?.teams?.data?.map((team) => {
                return (
                  <TeamContainerStyled key={team.id}>
                    <JourneyAddTeamsModalTeamItem
                      isAdded={newlyAddedTeams.includes(team?.id)}
                      name={team?.name}
                      total={team?.teamUsers?.total}
                      id={team?.id}
                      onAddTeam={handleAddTeam}
                    />
                  </TeamContainerStyled>
                );
              })}
            </>
          )}
        </TeamListStyled>
        {isBannerVisible && <JourneyAddTeamsModalBanner allUsers={allUsersFromNewTeams} />}
      </Modal.Contents>
      <Modal.Actions>
        <Button variant="ghost" onClick={handleClose} title="Cancel">
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} title="Save" $loading={mutationLoading}>
          Save
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
